import JSZip from 'jszip';

// Generate unique ID
export const generateId = () => Math.random().toString(36).substr(2, 9);

// Parse log line
export const parseLogLine = (line, index, containerName = null) => {
  // First try: container | timestamp message format
  const regexWithContainer = /^([a-zA-Z0-9_-]+)\s+\|\s+(\d{4}-\d{2}-\d{2}T[\d:.]+Z?)\s+(.*)$/;
  const matchWithContainer = line.match(regexWithContainer);

  if (matchWithContainer) {
    const container = matchWithContainer[1].trim();
    return {
      id: index,
      container: container,
      timestamp: matchWithContainer[2],
      tsValue: new Date(matchWithContainer[2]).getTime(),
      message: matchWithContainer[3],
      raw: line
    };
  }

  // Second try: timestamp-only format (for .log files from archives)
  const regexTimestampOnly = /^(\d{4}-\d{2}-\d{2}T[\d:.]+Z?)\s+(.*)$/;
  const matchTimestampOnly = line.match(regexTimestampOnly);

  if (matchTimestampOnly) {
    return {
      id: index,
      container: containerName || 'default',
      timestamp: matchTimestampOnly[1],
      tsValue: new Date(matchTimestampOnly[1]).getTime(),
      message: matchTimestampOnly[2],
      raw: line
    };
  }

  // Handle unstructured lines
  if (line.length > 0) {
    return {
      id: index,
      container: containerName || 'system',
      timestamp: '',
      tsValue: 0,
      message: line,
      raw: line
    };
  }

  return null;
};

// Generate sample data for demo
export const generateSampleData = () => {
  const containers = ['api-gateway', 'auth-service', 'db-postgres', 'redis-cache', 'frontend-react'];
  const messages = [
    'Request received GET /api/v1/users',
    'Database connection established',
    'Cache miss for key: user_session_123',
    'Rendering component Dashboard',
    'Error: Connection timed out',
    'Health check passed',
    'User logged in: admin',
    'Backup started',
    'Transaction commit successful'
  ];

  let text = '';
  const now = new Date();

  for (let i = 0; i < 200; i++) {
    const container = containers[Math.floor(Math.random() * containers.length)];
    const msg = messages[Math.floor(Math.random() * messages.length)];
    const time = new Date(now.getTime() - (200 - i) * 1000 * Math.floor(Math.random() * 10)).toISOString();
    text += `${container} | ${time} ${msg} [req_id=${Math.floor(Math.random()*1000)}]\n`;
  }

  return text;
};

// Highlight text with search query
export const highlightText = (text, query) => {
  if (!query) return text.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const safeText = text.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  return safeText.replace(regex, '<span class="bg-yellow-600/50 text-white px-0.5 rounded">$1</span>');
};

// Format timestamp based on display mode
export const formatTimestamp = (timestamp, mode) => {
  if (!timestamp || mode === 'hidden') return '';
  if (mode === 'time-only') {
    // Extract time portion from ISO string (e.g., "2025-11-18T08:00:00Z" -> "08:00:00")
    return timestamp.split('T')[1]?.replace('Z', '') || timestamp;
  }
  return timestamp; // 'full' mode
};

// Extract filename without extension and path
export const getFileNameWithoutExt = (filename) => {
  const parts = filename.split('/');
  const basename = parts[parts.length - 1];
  return basename.replace(/\.log$/, '');
};

// Process a single log file
export const processLogFile = (filename, content) => {
  const lines = content.split(/\r?\n/);
  const logs = [];
  const containerName = getFileNameWithoutExt(filename);
  
  lines.forEach((line, index) => {
    if (!line.trim()) return;
    const parsed = parseLogLine(line, logs.length, containerName);
    if (parsed) {
      logs.push(parsed);
    }
  });
  
  return {
    filename,
    containerName,
    logs,
    lineCount: logs.length
  };
};

// Process zip archive containing log files
export const processZipFile = async (file) => {
  const zip = new JSZip();
  const zipContent = await zip.loadAsync(file);
  const logFiles = [];
  
  // Find all .log files in the zip
  for (const [filename, zipEntry] of Object.entries(zipContent.files)) {
    if (!zipEntry.dir && filename.toLowerCase().endsWith('.log')) {
      const content = await zipEntry.async('text');
      const processedFile = processLogFile(filename, content);
      if (processedFile.logs.length > 0) {
        logFiles.push(processedFile);
      }
    }
  }
  
  return logFiles;
};

// Process single log file upload
export const processSingleLogFile = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        const processedFile = processLogFile(file.name, content);
        resolve([processedFile]);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
};

// Main function to process uploaded files (zip or log)
export const processUploadedFiles = async (files) => {
  const allLogFiles = [];
  
  for (const file of files) {
    if (file.name.toLowerCase().endsWith('.zip')) {
      const zipLogFiles = await processZipFile(file);
      allLogFiles.push(...zipLogFiles);
    } else if (file.name.toLowerCase().endsWith('.log')) {
      const logFiles = await processSingleLogFile(file);
      allLogFiles.push(...logFiles);
    }
  }
  
  return allLogFiles;
};

